import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath } from '../util/routes';
import { useConfiguration } from '../context/configurationContext';
import {
  finishNavigationInstrumentation,
  markNavigationStage,
  startNavigationInstrumentation,
} from '../util/navigationInstrumentation';

import { locationChanged, routeTransitionStart, routeTransitionEnd } from '../ducks/routing.duck';
import { needsProfileSetup } from '../util/profileSetup';

import { NamedRedirect } from '../components';

import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';

const isBanned = currentUser => {
  const isBrowser = typeof window !== 'undefined';
  // Future todo: currentUser?.attributes?.state === 'banned'
  return isBrowser && currentUser?.attributes?.banned === true;
};

const canShowComponent = props => {
  const { isAuthenticated, currentUser, route } = props;
  const { auth } = route;
  return !auth || (isAuthenticated && !isBanned(currentUser));
};

const ONBOARDING_ALLOWED_ROUTE_NAMES = [
  'ProfileSetupPage',
  'EmailVerificationPage',
  'ContactDetailsPage',
  'TermsOfServicePage',
  'PrivacyPolicyPage',
];

const ProfileSetupPendingPage = () => (
  <div
    style={{
      minHeight: '100vh',
      background: '#030303',
    }}
  />
);

const MIN_TRANSITION_MS = 220;

const preloadRouteComponent = route => {
  const { component: RouteComponent } = route;
  return typeof RouteComponent?.preload === 'function'
    ? Promise.resolve(RouteComponent.preload()).catch(e => {
        log.error(e, 'route-component-preload-failed', { routeName: route.name });
      })
    : Promise.resolve();
};

const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress;

  if (shouldLoadData) {
    return dispatch(loadData(match.params, location.search, config)).catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }

  return Promise.resolve();
};

const setPageScrollPosition = (location, delayed) => {
  if (!location.hash) {
    // No hash, scroll to top
    window.scroll({
      top: 0,
      left: 0,
    });
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.

      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      // A naive attempt to make a delayed call to scrollIntoView
      // Note: 300 milliseconds might not be enough, but adding too much delay
      // might affect user initiated scrolling.
      delayed = window.setTimeout(() => {
        const reTry = document.querySelector(location.hash);
        reTry?.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }, 300);
    }
  }
};

const handleLocationChanged = (dispatch, location, routeConfiguration, delayed) => {
  setPageScrollPosition(location, delayed);
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
};

/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 *
 * @component
 * @param {Object} props - The props
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {boolean} props.logoutInProgress - Whether the logout is in progress
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {propTypes.route} props.route - The route
 * @param {Array<propTypes.route} props.routeConfiguration - The route configuration
 * @param {Object} props.match - The match
 * @param {Object} props.match.params - The match params
 * @param {string} props.match.url - The match url
 * @param {Object} props.location - The location
 * @param {Object} props.location.search - The location search
 * @param {Object} props.staticContext - The static context
 * @param {Function} props.dispatch - The dispatch function of
 * @returns {JSX.Element} The RouteComponentRenderer component
 */
class RouteComponentRenderer extends Component {
  componentDidMount() {
    const { dispatch, location, routeConfiguration } = this.props;
    this.delayed = null;
    this.transitionEndTimeout = null;
    this.transitionSequence = 0;
    // Calling loadData on initial rendering (on client side).
    callLoadData(this.props);
    handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, location, routeConfiguration } = this.props;
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    if (prevProps.location !== this.props.location) {
      const transitionId = `${location.pathname}${location.search}${location.hash}:${Date.now()}:${
        this.transitionSequence
      }`;
      this.transitionSequence += 1;
      const startedAt = Date.now();

      dispatch(routeTransitionStart(location, transitionId));
      startNavigationInstrumentation({
        transitionId,
        pathname: `${location.pathname}${location.search}`,
      });

      const componentPreload = preloadRouteComponent(this.props.route).finally(() => {
        markNavigationStage(transitionId, 'chunkReadyAt');
      });
      const routeDataLoad = callLoadData(this.props).finally(() => {
        markNavigationStage(transitionId, 'dataReadyAt');
      });

      Promise.all([componentPreload, routeDataLoad]).finally(() => {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, MIN_TRANSITION_MS - elapsed);

        this.transitionEndTimeout = window.setTimeout(() => {
          dispatch(routeTransitionEnd(transitionId));
          finishNavigationInstrumentation(transitionId);
        }, remaining);
      });

      handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
    }
  }

  componentWillUnmount() {
    if (this.delayed) {
      window.clearTimeout(this.delayed);
    }

    if (this.transitionEndTimeout) {
      window.clearTimeout(this.transitionEndTimeout);
    }
  }

  render() {
    const {
      route,
      match,
      location,
      staticContext = {},
      currentUser,
      currentUserShowError,
      isAuthenticated,
    } = this.props;
    const { component: RouteComponent, authPage = 'SignupPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    if (!canShow) {
      staticContext.unauthorized = true;
    }

    const hasCurrentUser = !!currentUser?.id;
    const shouldWaitForCurrentUser =
      canShow &&
      isAuthenticated &&
      !hasCurrentUser &&
      !currentUserShowError &&
      !ONBOARDING_ALLOWED_ROUTE_NAMES.includes(route.name);
    const restrictedPageWithCurrentUser = !canShow && hasCurrentUser;
    // Banned users are redirected to LandingPage
    const isBannedFromAuthPages = restrictedPageWithCurrentUser && isBanned(currentUser);
    const shouldRedirectToProfileSetup =
      canShow &&
      isAuthenticated &&
      hasCurrentUser &&
      needsProfileSetup(currentUser) &&
      !ONBOARDING_ALLOWED_ROUTE_NAMES.includes(route.name);

    if (shouldRedirectToProfileSetup) {
      return <NamedRedirect name="ProfileSetupPage" />;
    }

    if (shouldWaitForCurrentUser) {
      return <ProfileSetupPendingPage />;
    }

    return canShow ? (
      <LoadableComponentErrorBoundary>
        <RouteComponent
          params={match.params}
          location={location}
          staticContext={staticContext}
          {...extraProps}
        />
      </LoadableComponentErrorBoundary>
    ) : isBannedFromAuthPages ? (
      <NamedRedirect name="LandingPage" />
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
}

const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.auth;
  const { currentUser, currentUserShowError } = state.user;
  return { isAuthenticated, logoutInProgress, currentUser, currentUserShowError };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);

/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { isAuthenticated, logoutInProgress, logLoadDataCalls } = props;

  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      route,
      routeConfiguration,
      config,
      logLoadDataCalls,
    };

    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    );
  };

  // Catch-all fallback for unmatched URLs. We render NotFoundPage in place
  // (keeping the original URL) via RouteComponentContainer so that loadData and
  // the server-side 404 status (staticContext.notfound) keep working.
  // NOTE: We deliberately do NOT use a `path: '*'` route in routeConfiguration,
  // because path-to-regexp v8's compile() cannot handle a bare '*'.
  const notFoundRoute = routeConfiguration.find(route => route.name === 'NotFoundPage');
  const renderFallbackRoute = () =>
    notFoundRoute ? (
      <Route
        render={matchProps => (
          <RouteComponentContainer
            isAuthenticated={isAuthenticated}
            logoutInProgress={logoutInProgress}
            route={notFoundRoute}
            routeConfiguration={routeConfiguration}
            config={config}
            logLoadDataCalls={logLoadDataCalls}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    ) : (
      <Route render={() => <NamedRedirect name="LandingPage" />} />
    );

  return (
    <Switch>
      {routeConfiguration.map(toRouteComponent)}
      {renderFallbackRoute()}
    </Switch>
  );
};

export default withRouter(Routes);
